/**
 * Table Tennis Scoring Engine - Player ID Based
 * Service derived from total points; points to win and serves per rotation configurable (tournament-level).
 */

import { getDeuceTotalPoints } from '../constants/scoring';

const DEFAULT_POINTS = 21;
const DEFAULT_SERVES = 2;

/**
 * Get the next server for doubles from total points (no separate serve counter).
 * @param {number} player1Score - Team 1's current score
 * @param {number} player2Score - Team 2's current score
 * @param {string} firstServer - Who served first in the game ('player1' or 'player2')
 * @param {string} firstServerPlayerId - Player ID who served first in the game
 * @param {array} team1Players - Array of player IDs for team 1 [player1Id, player2Id]
 * @param {array} team2Players - Array of player IDs for team 2 [player1Id, player2Id]
 * @param {object} options - { pointsToWin: 11|21, servesPerRotation: 2|5 }
 * @returns {object} {server, serverPlayerId, receiverPlayerId, servesInRotation, shouldSwitch}
 */
export function getNextServerDoublesByPlayerId(player1Score, player2Score, firstServer, firstServerPlayerId, team1Players, team2Players, options = {}) {
  const totalPoints = player1Score + player2Score;
  const pointsToWin = options.pointsToWin ?? DEFAULT_POINTS;
  const servesPerRotation = options.servesPerRotation ?? DEFAULT_SERVES;
  const deuceTotal = getDeuceTotalPoints(pointsToWin);
  const inDeuce = totalPoints >= deuceTotal;

  const getPlayerPosition = (playerId, teamPlayers) => {
    if (!teamPlayers || teamPlayers.length === 0) return -1;
    const index = teamPlayers.indexOf(playerId);
    return index >= 0 ? index : -1;
  };

  const getReceiverPlayerId = (serverPlayerId, t1, t2) => {
    const i1 = getPlayerPosition(serverPlayerId, t1);
    const i2 = getPlayerPosition(serverPlayerId, t2);
    if (i1 >= 0) return t2 && t2[i1] ? t2[i1] : null;
    if (i2 >= 0) return t1 && t1[i2] ? t1[i2] : null;
    return null;
  };

  const t1 = team1Players || [];
  const t2 = team2Players || [];
  let order;
  if (firstServer === 'player1') {
    const pos = getPlayerPosition(firstServerPlayerId, t1);
    const pos2 = pos >= 0 ? 1 - pos : 0;
    order = [t1[pos] ?? t1[0], t2[pos] ?? t2[0], t1[pos2] ?? t1[1] ?? t1[0], t2[pos2] ?? t2[1] ?? t2[0]];
  } else {
    const pos = getPlayerPosition(firstServerPlayerId, t2);
    const pos2 = pos >= 0 ? 1 - pos : 0;
    order = [t2[pos] ?? t2[0], t1[pos] ?? t1[0], t2[pos2] ?? t2[1] ?? t2[0], t1[pos2] ?? t1[1] ?? t1[0]];
  }

  const index = inDeuce ? (totalPoints % 4) : (Math.floor(totalPoints / servesPerRotation) % 4);
  const serverPlayerId = order[index] ?? order[0];
  const server = (t1.includes(serverPlayerId) ? 'player1' : 'player2');
  const receiverPlayerId = getReceiverPlayerId(serverPlayerId, t1, t2);
  const servesInRotation = inDeuce ? (totalPoints % 2) : (totalPoints % servesPerRotation);
  const shouldSwitch = inDeuce || (totalPoints % servesPerRotation) === servesPerRotation - 1;

  return {
    server,
    serverPlayerId,
    receiverPlayerId,
    servesInRotation,
    shouldSwitch
  };
}
